import { parseFormattedPhoneNumber } from '../../util/ValidationUtils';
import { LocationWizardData } from './types';

/** A single estimated value from the enrichment response (lot size / space count). */
export interface EnrichmentEstimatedValue {
  value: number | string | null;
  unit: string | null;
  source: string | null;
  confidence: string | null;
}

/** Shape of `POST /api/location-enrichment` (`LocationEnrichmentResponseVO`). */
export interface LocationEnrichmentResponse {
  location: {
    formattedAddress: string;
    placeId: string | null;
    businessName: string | null;
    phoneNumber: string | null;
    website: string | null;
    latitude: number;
    longitude: number;
  };
  description: string;
  /** Always a subset of `LocationFeatureType.name()` enum values. */
  suggestedFeatures: string[];
  observedEvidence: string[];
  generatedObservations: string[];
  estimatedFields: {
    lotSize: EnrichmentEstimatedValue | null;
    spaceCount: EnrichmentEstimatedValue | null;
  };
  warnings: string[];
  confidence: {
    analysisThresholdPassed: boolean;
    generationInvoked: boolean;
    overall: string;
  };
  sourceAttribution: {
    locationMetadata: string | null;
    imagery: string[] | null;
    imageAnalysis: string | null;
    generation: string | null;
  };
  fallback: {
    used: boolean;
    skipReason: string | null;
  };
}

/** Enum value the model uses for the 24/7-access feature (owned by Step 2's `has247Access`). */
const TWENTY_FOUR_SEVEN_ACCESS = 'TWENTY_FOUR_SEVEN_ACCESS';

/**
 * Maps `LocationFeatureType.name()` enum values to the display labels used by
 * `LocationFeatures.OPTIONS` / the Features step. `TWENTY_FOUR_SEVEN_ACCESS` is
 * intentionally excluded — it is handled via `has247Access` (Step 2 owns it).
 * Unknown enum values are dropped by the mapper (defensive against new members).
 */
export const FEATURE_ENUM_TO_LABEL: Record<string, string> = {
  MANNED_SECURITY: 'Manned Security',
  FENCED_OR_GATED: 'Fenced / Gated',
  YARD_CAMERAS: 'Yard Cameras',
  GATE_CAMERAS: 'Gate Cameras',
  LIGHTS: 'Lights',
  PAVED: 'Paved',
  M_AND_R_OFFERED: 'M & R Offered',
  SIDE_OR_TOP_PICK: 'Side-pick / Top-Pick',
  DAILY_INVENTORY_REPORT: 'Daily Inventory Report',
  OVERNIGHT_TRUCK_PARKING: 'Overnight Truck Parking',
  RESTROOMS: 'Restrooms',
};

/**
 * Normalizes a free-form phone number from the enrichment response to the E.164
 * format the wizard's phone field expects (e.g. "+1-713-555-0123" → "+17135550123").
 *
 * @returns The E.164 string, or null if the input cannot be confidently normalized.
 */
export const normalizeEnrichmentPhone = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const digits = parseFormattedPhoneNumber(raw);
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
};

/**
 * Parses an estimated `spaceCount.value` (number | string | null) to an integer.
 * Numeric ranges like "20-30" resolve to the rounded midpoint (25). Categorical
 * values (e.g. "medium") and unparseable input return null (field left unchanged).
 */
export const parseEstimatedSpaceCount = (value: number | string | null | undefined): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value) : null;
  }
  const trimmed = value.trim();
  const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rangeMatch) {
    const low = Number(rangeMatch[1]);
    const high = Number(rangeMatch[2]);
    return Math.round((low + high) / 2);
  }
  const singleMatch = trimmed.match(/^\d+$/);
  if (singleMatch) {
    return Number(trimmed);
  }
  return null;
};

/**
 * Translates the enrichment response into a partial {@link LocationWizardData}
 * suitable for `updateData`. Per the agreed field mapping:
 *  - Address & coordinates from the response are ignored (the user-selected
 *    address is the source of truth).
 *  - `businessName`/`phoneNumber` are applied only when present.
 *  - `description`, features, and space count are always replaced.
 *  - 24/7 access is mapped onto `has247Access`; all other features onto
 *    `locationFeatures` using the display labels.
 */
export const mapEnrichmentToWizardData = (
  response: LocationEnrichmentResponse,
  current: LocationWizardData
): Partial<LocationWizardData> => {
  const partial: Partial<LocationWizardData> = {};

  // Name — overwrite only when the response has a non-blank business name.
  const businessName = response.location?.businessName?.trim();
  if (businessName) {
    partial.locationName = businessName;
  }

  // Phone — overwrite only when it normalizes to a valid E.164 number.
  const phone = normalizeEnrichmentPhone(response.location?.phoneNumber);
  if (phone) {
    partial.phoneNumber = phone;
  }

  // Description — always replaced (Step 2).
  partial.locationDescription = response.description || '';

  // Features — rebuild from suggestedFeatures. 24/7 → has247Access, the rest →
  // locationFeatures (mapped to display labels).
  const suggested = response.suggestedFeatures || [];
  partial.has247Access = suggested.includes(TWENTY_FOUR_SEVEN_ACCESS);

  const mappedLabels = suggested
    .filter((name) => name !== TWENTY_FOUR_SEVEN_ACCESS)
    .map((name) => FEATURE_ENUM_TO_LABEL[name])
    .filter((label): label is string => Boolean(label));
  const uniqueLabels = Array.from(new Set(mappedLabels));
  // Keep sub-field details the supplier already entered for features autofill re-suggests.
  const currentByFeature = new Map((current.locationFeatures || []).map((f) => [f.feature, f]));
  partial.locationFeatures = uniqueLabels.map((feature) => {
    const details = currentByFeature.get(feature)?.details;
    return details ? { feature, details } : { feature };
  });

  // Space count — replace when parseable (midpoint for ranges).
  const spaceCount = parseEstimatedSpaceCount(response.estimatedFields?.spaceCount?.value);
  if (spaceCount != null) {
    partial.totalNumberOfSpaces = spaceCount;
    // Keep the minimum within bounds if it now exceeds the estimated total.
    if (current.minNumberOfSpaces != null && current.minNumberOfSpaces > spaceCount) {
      partial.minNumberOfSpaces = spaceCount;
    }
  }

  return partial;
};

